import type { TFunction } from "i18next";
import type { AccessList, NamedGroup, SystemGroup } from "../../../api/access/accessModel";

/**
 * Group names as the screens show them. DMW's own groups read in the user's language
 * (PV3-8: Administrators, Curators, Contributors, Readers, Everyone); other groups by the
 * name people gave them. A path joins a group and the groups around it, outermost first.
 */

export function systemGroupName(t: TFunction, system: SystemGroup): string {
  return t(`access.systemGroups.${system}`);
}

export interface GroupNamer {
  /** The group's name; a group the list doesn't know by the name the server served with it, else as "a removed group". */
  readonly name: (groupId: string, served?: string) => string;
  /** A source's path: "Historians › Student assistants". */
  readonly path: (groupIds: readonly string[]) => string;
}

/** Names from a scope's list, with any further groups a panel names. */
export function groupNamer(t: TFunction, list: AccessList, more: ReadonlyMap<string, NamedGroup> = new Map()): GroupNamer {
  const systems = new Map(list.groups.flatMap((group) => (group.system === null ? [] : [[group.groupId, group.system] as const])));
  const name = (groupId: string, served?: string) => {
    const system = systems.get(groupId);
    if (system !== undefined) return systemGroupName(t, system);
    return list.namedGroups.get(groupId)?.name ?? more.get(groupId)?.name ?? served ?? t("access.removedGroup");
  };
  return { name, path: (groupIds) => groupIds.map((groupId) => name(groupId)).join(" › ") };
}
